import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useTheme } from '@/contexts/ThemeContext';
import { Loader2, TrendingUp, Activity, BarChart3, Target, Gauge, AlertTriangle, CheckCircle } from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, PieChart, Pie, Cell
} from 'recharts';

function KPICard({ label, value, sub, icon: Icon, color }) {
  return (
    <div className="p-4 rounded-lg border transition-all hover:scale-[1.02]" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}
      data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] font-medium uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>{label}</p>
          <p className="text-2xl font-bold mt-1" style={{ color: color || 'var(--text-primary)' }}>{value}</p>
          {sub && <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{sub}</p>}
        </div>
        {Icon && <Icon className="w-5 h-5" style={{ color: color || 'var(--accent-primary)', opacity: 0.7 }} />}
      </div>
    </div>
  );
}

const STATUS_PIE_COLORS = {
  NOT_STARTED: '#64748b',
  ON_TRACK: '#10b981',
  AHEAD: '#06b6d4',
  BEHIND: '#ef4444',
  COMPLETED: '#22c55e',
  SUSPENDED: '#f59e0b',
  IN_PROGRESS: '#3b82f6',
};

export default function FCAnalytics({ projectId }) {
  const { isDark } = useTheme();
  const [summary, setSummary] = useState(null);
  const [scurve, setScurve] = useState([]);
  const [loading, setLoading] = useState(false);
  const [discFilter, setDiscFilter] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, curveRes] = await Promise.all([
        api.get(`/v1/projects/${projectId}/fc/summary`),
        api.get(`/v1/projects/${projectId}/fc/summary/s-curve${discFilter ? `?discipline=${discFilter}` : ''}`)
      ]);
      setSummary(sumRes.data);
      setScurve(curveRes.data?.s_curve_data || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [projectId, discFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>;
  if (!summary) return <div className="text-center py-20 text-sm" style={{ color: 'var(--text-muted)' }}>No data available. Add activities and generate plans first.</div>;

  const disciplines = summary.byDiscipline || [];
  const statusDist = summary.statusDistribution || {};
  const statusPieData = Object.entries(statusDist).map(([key, val]) => ({ name: key, value: val, fill: STATUS_PIE_COLORS[key] || '#94a3b8' }));

  return (
    <div className="space-y-4" data-testid="fc-analytics">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPICard label="Total Activities" value={summary.totalActivities} sub={`${summary.totalEquivalentManhours?.toLocaleString()} equiv. MH`} icon={Activity} />
        <KPICard label="Overall Planned" value={`${summary.overallPlannedProgressPct}%`} icon={Target} color="#3b82f6" />
        <KPICard label="Overall Actual" value={`${summary.overallActualProgressPct}%`} icon={TrendingUp} color="#10b981" />
        <KPICard label="Overall SPI" value={summary.overallSPI?.toFixed(3)}
          sub={`Variance: ${summary.overallVariancePct >= 0 ? '+' : ''}${summary.overallVariancePct}%`}
          icon={Gauge}
          color={summary.overallSPI >= 1 ? '#10b981' : summary.overallSPI >= 0.9 ? '#f59e0b' : '#ef4444'} />
      </div>

      {/* Secondary KPIs */}
      <div className="grid grid-cols-3 gap-3">
        <KPICard label="Completed" value={summary.completed} icon={CheckCircle} color="#22c55e" />
        <KPICard label="Behind Schedule" value={summary.behind} icon={AlertTriangle} color="#ef4444" />
        <KPICard label="Ahead of Schedule" value={summary.ahead} icon={TrendingUp} color="#06b6d4" />
      </div>

      {/* S-Curve Chart */}
      <div className="p-4 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>S-Curve (Weighted Progress)</h4>
          <select value={discFilter} onChange={e => setDiscFilter(e.target.value)}
            className="px-2 py-1 rounded text-xs border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            data-testid="scurve-disc-filter">
            <option value="">All Disciplines</option>
            {disciplines.map(d => <option key={d.disciplineCode} value={d.disciplineCode}>{d.disciplineCode} - {d.disciplineName}</option>)}
          </select>
        </div>
        {scurve.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-sm" style={{ color: 'var(--text-muted)' }}>No S-curve data available</div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={scurve} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
              <defs>
                <linearGradient id="fcPlanGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="fcActualGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#1e293b' : '#e2e8f0'} />
              <XAxis dataKey="periodLabel" tick={{ fontSize: 9, fill: isDark ? '#94a3b8' : '#64748b' }} interval="preserveStartEnd" angle={-45} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} domain={[0, 'auto']} tickFormatter={v => `${v}%`} />
              <Tooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 11 }}
                formatter={(v, name) => [`${typeof v === 'number' ? v.toFixed(2) : v}%`, name]} />
              <Area type="monotone" dataKey="cumulativePlannedPct" stroke="#3B82F6" fill="url(#fcPlanGrad)" strokeWidth={2} name="Planned" />
              <Area type="monotone" dataKey="cumulativeActualPct" stroke="#10B981" fill="url(#fcActualGrad)" strokeWidth={2} name="Actual" />
              <Legend />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Bottom Row: Discipline Breakdown + Status Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Discipline Breakdown */}
        <div className="p-4 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Discipline Breakdown</h4>
          {disciplines.length === 0 ? (
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>No data</p>
          ) : (
            <div className="space-y-2">
              {disciplines.map(d => (
                <div key={d.disciplineCode} className="p-2 rounded border" style={{ borderColor: 'var(--border-color)' }}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{d.disciplineCode} - {d.disciplineName}</span>
                    <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{d.activityCount} acts | {d.disciplineWeightagePct?.toFixed(1)}% wt</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'var(--border-color)' }}>
                      <div className="h-full rounded-full" style={{ width: `${Math.min(d.plannedProgressPct || 0, 100)}%`, background: '#3b82f6', opacity: 0.5 }} />
                    </div>
                    <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'var(--border-color)' }}>
                      <div className="h-full rounded-full" style={{ width: `${Math.min(d.actualProgressPct || 0, 100)}%`, background: '#10b981' }} />
                    </div>
                    <span className="text-[10px] font-mono min-w-[60px] text-right"
                      style={{ color: d.spi >= 1 ? '#10b981' : '#ef4444' }}>
                      SPI {d.spi?.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-[10px]" style={{ color: 'var(--text-muted)' }}>
                    <span>Plan: <span style={{ color: '#3b82f6' }}>{d.plannedProgressPct?.toFixed(1)}%</span></span>
                    <span>Actual: <span style={{ color: '#10b981' }}>{d.actualProgressPct?.toFixed(1)}%</span></span>
                    <span>Var: <span style={{ color: d.variancePct >= 0 ? '#10b981' : '#ef4444' }}>{d.variancePct >= 0 ? '+' : ''}{d.variancePct?.toFixed(1)}%</span></span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Status Distribution */}
        <div className="p-4 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Status Distribution</h4>
          {statusPieData.length === 0 ? (
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>No data</p>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={statusPieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value">
                    {statusPieData.map((entry, idx) => <Cell key={idx} fill={entry.fill} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-2 mt-2 justify-center">
                {statusPieData.map(s => (
                  <span key={s.name} className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--text-muted)' }}>
                    <span className="w-2 h-2 rounded-full" style={{ background: s.fill }} />
                    {s.name}: {s.value}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
